import { useTranslation } from 'react-i18next'
import { isEmpty } from 'ramda'
import cn from 'classnames/bind'

import { Table, useCopyColumnsWidths } from 'app/common/ui-components/table'
import Loading from 'app/common/components/Loader'
import { BannerContent } from 'app/common/components-next/banner'
import EmptyWarn from 'app/common/layouts/EmptyWarn'
import Sticky from 'app/common/templates-next/sticky'
import { PageTemplateContent } from 'app/common/templates-next/page-template'

import usePaymentOrders from './usePaymentOrders'
import usePaymentOrderItems from './usePaymentOrderItems'
import PaymentOrdersTHead from './PaymentOrdersTHead'
import PaymentOrdersTableRowItem from './PaymentOrdersTableRowItem'
import styles from './PaymentOrdersTable.module.scss';

const cx = cn.bind(styles)

type Props = {
  personId: number
  schoolId?: string | null
  bannerPlaceId?: string
  getPaymentPath?: (s: string) => string
}

const PaymentOrdersTable = ({ personId, schoolId, bannerPlaceId, getPaymentPath }: Props) => {
  const { t } = useTranslation('finances')
  const [{ state, loading }] = usePaymentOrders(
    personId,
    schoolId ? parseInt(schoolId, 10) : undefined,
  )

  const paymentOrdersItems = usePaymentOrderItems(state)

  const columns: Array<string> = t('finances_payment_orders_table_columns', { returnObjects: true })

  // const updateKey = useMemo(() => ([]), [tableSettings, dishes]); // eslint-disable-line react-hooks/exhaustive-deps
  const [refFrom, refTo] = useCopyColumnsWidths(/* updateKey */)

  const nothing = isEmpty(state)
  const emptyData = isEmpty(paymentOrdersItems)

  return (
    <>
      <Sticky>
        {(ref, sticky, shadow) => (
          <PageTemplateContent
            className={cx({ sticky })}
            shadowBottom={shadow}
            reference={ref}
            skipPadding
          >
            <Table className={styles.table}>
              <PaymentOrdersTHead columns={columns} reference={refTo} />
            </Table>

            {bannerPlaceId && (
              <BannerContent
                placeId="d099e4c6-f44b-42ef-b5f9-a1d52c27dee5"
                className={styles.bannerPlaceholder}
                displayAsPlaceholder
                height={0}
              />
            )}

            {loading && !nothing && <Loading className={styles.loadingOverContent} />}
          </PageTemplateContent>
        )}
      </Sticky>
      <PageTemplateContent className={cx(styles.flex, styles.layout)} skipPadding>
        <div className={styles.tableContainer}>
          {emptyData && !loading && <EmptyWarn>{t('empty_payment_orders')}</EmptyWarn>}
          {loading && <Loading />}

          {!nothing && (
            <Table className={styles.table}>
              <PaymentOrdersTHead columns={columns} reference={refFrom} hidden dummy />
              <tbody>
                {paymentOrdersItems.map(({ id, sortable, ...props }) => (
                  <PaymentOrdersTableRowItem
                    key={id}
                    id={id}
                    loading={loading}
                    personId={personId}
                    getPaymentPath={getPaymentPath}
                    {...props}
                  />
                ))}
              </tbody>
            </Table>
          )}
        </div>

        {bannerPlaceId && (
          <BannerContent
            placeId="d099e4c6-f44b-42ef-b5f9-a1d52c27dee5"
            className={styles.bannerPlaceholder}
            displayAsPlaceholder
            height={0}
          />
        )}
      </PageTemplateContent>
    </>
  )
}

export default PaymentOrdersTable
